import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonModal,
  IonSearchbar,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';
import { useEffect, useMemo, useState } from 'react';
import { useEnqueueMaster, useMasters } from '../../lib/data/queries.js';
import { toUserMessage } from '../../lib/errors.js';

interface FoodRow {
  id: string;
  name: string;
  category?: string | null;
  archived_at?: string | null;
}
export interface CassettoVoce {
  id: string;
  version?: number;
}

interface CassettoProductModalProps {
  isOpen: boolean;
  /** etichetta del cassetto (es. "Giornaliera"), solo per il titolo */
  label: string;
  voci: CassettoVoce[];
  currentProductIds: string[];
  onDismiss: () => void;
  onSaved?: () => void;
}

// Imposta il/i prodotto/i (detersivo) del cassetto. Fonte canonica:
// foods(category=detersivi). Salva applicando product_ids
// a TUTTE le voci del cassetto via patch parziale (non azzera gli altri campi).
export function CassettoProductModal({
  isOpen,
  label,
  voci,
  currentProductIds,
  onDismiss,
  onSaved,
}: CassettoProductModalProps): JSX.Element {
  const createMaster = useEnqueueMaster();
  const { data: foods = [] } = useMasters<FoodRow>('foods');
  const [selected, setSelected] = useState<string[]>(currentProductIds);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [presentToast] = useIonToast();
  const setToast = (m: string): void => {
    void presentToast({ message: m, duration: 1600, position: 'top' });
  };

  useEffect(() => {
    if (isOpen) setSelected(currentProductIds);
  }, [isOpen, currentProductIds]);

  const options = useMemo(() => {
    const detergents = foods
      .filter((f) => f.category === 'detersivi' && !f.archived_at)
      .map((f) => ({ id: f.id, name: f.name }));
    const q = query.trim().toLowerCase();
    return q ? detergents.filter((o) => o.name.toLowerCase().includes(q)) : detergents;
  }, [foods, query]);

  function toggle(id: string): void {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  }

  async function save(): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      for (const v of voci) {
        await createMaster.mutateAsync({
          table: 'cleaning_schedules',
          id: v.id,
          op: 'update',
          base_version: v.version ?? 0,
          patch: {
            id: v.id,
            product_ids: selected,
            product_id: selected[0] ?? null,
          },
        });
      }
      setToast('Prodotto del cassetto aggiornato');
      onSaved?.();
      onDismiss();
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDismiss}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={onDismiss} disabled={busy}>Annulla</IonButton>
          </IonButtons>
          <IonTitle>Prodotto · {label}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => void save()} disabled={busy} strong>Salva</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div style={{ padding: '8px 12px 4px' }}>
          <div style={{ fontSize: 12, color: '#64748b', padding: '0 4px 6px', lineHeight: 1.4 }}>
            Detersivo/i usato/i per questo cassetto. Compare nel registro come
            prodotto utilizzato. Si applica a tutte le voci del cassetto.
          </div>
          <IonSearchbar
            value={query}
            onIonInput={(e) => setQuery(String(e.detail.value ?? ''))}
            placeholder="Cerca detersivo"
          />
        </div>
        <div style={{ padding: '0 12px 24px' }}>
          {options.length === 0 ? (
            <div style={{ background: '#fff', padding: 16, borderRadius: 12, color: '#94a3b8', fontStyle: 'italic', fontSize: 13 }}>
              Nessun detersivo a magazzino. Aggiungilo da Pulizie → Prodotti.
            </div>
          ) : (
            options.map((o) => {
              const active = selected.includes(o.id);
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => toggle(o.id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    background: active ? '#ecfdf5' : '#fff',
                    border: `1px solid ${active ? '#0d9488' : '#e2e8f0'}`,
                    borderRadius: 12,
                    margin: '6px 0',
                    padding: '12px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      width: 22, height: 22, borderRadius: 6,
                      border: `2px solid ${active ? '#0d9488' : '#cbd5e1'}`,
                      background: active ? '#0d9488' : '#fff',
                      color: '#fff', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 700,
                    }}
                  >
                    {active ? '✓' : ''}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{o.name}</div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </IonContent>
    </IonModal>
  );
}
